import { useEffect, useRef, useState } from "react";

import { AlertTriangle, ChevronDown, ChevronUp, Monitor, Users } from "lucide-react";
import { LiveAudioVisualizer } from "react-audio-visualize";

import { cn } from "@/utils/ui";

import { ReactComponent as PauseIcon } from "@assets/svg/media-pause-filled.svg";
import { ReactComponent as PlayIcon } from "@assets/svg/media-play-filled.svg";

import { Button } from "@components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { useElementWidth } from "../hooks/useElementWidth";
import { useThemeColor } from "../hooks/useThemeColor";
import type { SessionMode } from "../types/sessionMode";
import { ConsentInfoTooltip } from "./ConsentInfoTooltip";
import {
  LiveTranscriptionFeed,
  LiveTranscriptionSegment,
} from "./LiveTranscriptionFeed";
import { TrackWaveform } from "./TrackWaveform";

// Shared waveform bar geometry so the recording (live) and paused (idle)
// waveforms line up — same bar thickness and gap in both states.
const WAVE_BAR_WIDTH = 4; // px
const WAVE_BAR_GAP = 3; // px

/** Format elapsed milliseconds as HH:MM:SS (e.g. "00:14:32"). */
export const formatDuration = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((n) => n.toString().padStart(2, "0"))
    .join(":");
};

/** Persistence state of the in-session Notes, driving the header label. */
export type NotesSaveState = "idle" | "saving" | "saved" | "error";

/**
 * Derive the Notes header label from the save state. Pure so it can be unit
 * tested without a clock. `now` is passed in for the relative-time cases.
 *  - idle, never saved   → "Saved automatically" (the default affordance)
 *  - saving              → "Saving…"
 *  - error               → "Couldn't save"
 *  - saved <10s ago      → "Saved just now"
 *  - saved earlier       → "Saved N minute(s)/hour(s) ago"
 */
export const formatSavedLabel = (
  state: NotesSaveState,
  lastSavedAt: number | null,
  now: number
): string => {
  if (state === "saving") return "Saving…";
  if (state === "error") return "Couldn't save";
  if (!lastSavedAt) return "Saved automatically";

  const elapsedMs = Math.max(0, now - lastSavedAt);
  const minutes = Math.floor(elapsedMs / 60000);
  if (minutes < 1) return "Saved just now";
  if (minutes < 60) return `Saved ${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  return `Saved ${hours} hour${hours === 1 ? "" : "s"} ago`;
};

type ScribeSessionProps = {
  isPaused: boolean;
  /** In person vs telehealth (MDRS-268) — drives the header mode chip. */
  sessionMode: SessionMode;
  /**
   * True when this is a telehealth session and the shared call tab stopped
   * sharing mid-session — shows the "Call audio stopped" banner.
   */
  callAudioLost?: boolean;
  /** Re-share the call tab after `callAudioLost` (telehealth only). */
  onReshareCallAudio?: () => void;
  /**
   * Telehealth only (MDRS-268): the isolated mic track and the shared
   * call-tab track, each driving their own source card + level meter (the
   * "You" / "Everyone on the call" row) in place of the single waveform.
   */
  micTrack?: MediaStreamTrack | null;
  callAudioTrack?: MediaStreamTrack | null;
  /**
   * The shared source's real label (e.g. "Zoom Meeting - Google Chrome"),
   * read from the picker's video track — see useCallAudioShare.sourceLabel.
   * Null when the browser reports no label; falls back to a generic caption.
   */
  callAudioSourceLabel?: string | null;
  /** Elapsed session time in ms — rendered as the large HH:MM:SS timer. */
  elapsedMs: number;
  /** Powers the LiveAudioVisualizer; null while paused/tearing down. */
  mediaRecorder: MediaRecorder | null;
  inputDeviceLabel: string;
  segments: LiveTranscriptionSegment[];
  interim?: LiveTranscriptionSegment | null;
  /**
   * True while re-hydrating the live feed after returning to the scribe tab
   * mid-session and the accumulated turns aren't on screen yet —
   * shows a "Restoring transcription…" affordance instead of a blank feed.
   */
  isRestoringFeed?: boolean;
  isConnecting?: boolean;
  /** Show provisional live speaker labels in the feed. */
  showLiveSpeakers?: boolean;
  notes: string;
  onNotesChange: (value: string) => void;
  /** Notes autosave state — drives the "Saved …" header label. */
  notesSaveState: NotesSaveState;
  /** Epoch ms of the last successful notes save, or null if never saved. */
  notesLastSavedAt: number | null;
  waveformCollapsed: boolean;
  onToggleWaveform: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onCancel: () => void;
};

/**
 * The in-panel live Scribe session view (MDRS-116, states A/B/C). Presentational
 * only — all recording logic lives in `useRecordingSession`. Renders the timer +
 * controls, the collapsible waveform (or a compact "Recording" badge when
 * collapsed), the plain live transcription feed, and a local notes textarea.
 */
export const ScribeSession = ({
  isPaused,
  sessionMode,
  callAudioLost = false,
  onReshareCallAudio,
  micTrack = null,
  callAudioTrack = null,
  callAudioSourceLabel = null,
  elapsedMs,
  mediaRecorder,
  inputDeviceLabel,
  segments,
  interim,
  isRestoringFeed = false,
  isConnecting = false,
  showLiveSpeakers = true,
  notes,
  onNotesChange,
  notesSaveState,
  notesLastSavedAt,
  waveformCollapsed,
  onToggleWaveform,
  onPause,
  onResume,
  onStop,
  onCancel,
}: ScribeSessionProps) => {
  // Measure the waveform container so the visualizer spans the full panel width
  // (the design waveform runs edge-to-edge, not a fixed 640px strip). The lib
  // needs a numeric canvas width, so we track the container's live width.
  const waveContainerRef = useRef<HTMLDivElement>(null);
  // Re-measures when the waveform is shown again after being collapsed.
  const waveWidth = useElementWidth(waveContainerRef, [waveformCollapsed]);

  // Recording waveform colour from the `--primary` theme token (resolved at
  // runtime — the visualizer needs a literal colour, not a class). Fallbacks
  // are the current ramp's computed hex — keep in sync if index.css changes.
  const waveColor = useThemeColor("--primary-300", "hsl(242 39% 62%)");
  // Telehealth per-source waveform colours (MDRS-268) — "You" (mic, success)
  // and "Everyone on the call" (shared tab, secondary/blue — not the
  // deprecated purple accent), resolved the same way.
  const micWaveColor = useThemeColor("--success-600", "hsl(120 86% 31%)");
  const callWaveColor = useThemeColor("--secondary-600", "hsl(210 100% 36%)");

  // Tick a clock so the relative "Saved N minutes ago" label refreshes while a
  // save timestamp exists. 20s cadence keeps the minute rollover close enough
  // without a per-second re-render. `now` advances only from the interval
  // callback (keeping render pure); a just-saved timestamp can exceed a stale
  // `now`, but formatSavedLabel clamps that to "Saved just now".
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (notesLastSavedAt === null) return;
    const interval = setInterval(() => setNow(Date.now()), 20000);
    return () => clearInterval(interval);
  }, [notesLastSavedAt]);
  const savedLabel = formatSavedLabel(notesSaveState, notesLastSavedAt, now);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Card header bar — matches the section-panel header treatment
          (background-raised fill, bottom border). */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-background-raised px-5 py-3.5">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">Scribe session</h2>
          <ConsentInfoTooltip />
        </div>
        {/* Mode indicator (MDRS-268) — display-only; the mode is locked once
            the session starts (chosen in the consent modal). */}
        <span
          title={
            sessionMode === "telehealth" ? "Telehealth session" : "In person session"
          }
          className="inline-flex items-center gap-1.5 rounded-full bg-background-white px-2.5 py-1 text-xs font-medium text-foreground-muted shadow-sm"
        >
          {sessionMode === "telehealth" ? (
            <Monitor className="size-3.5" />
          ) : (
            <Users className="size-3.5" />
          )}
          {sessionMode === "telehealth" ? "Telehealth" : "In person"}
        </span>
      </div>

      {/* Telehealth: the shared call tab stopped sharing mid-session — the mic
          keeps recording the examiner, but the examinee's side of the call is
          no longer captured until re-shared. */}
      {sessionMode === "telehealth" && callAudioLost && (
        <div className="flex shrink-0 items-center gap-2.5 border-b border-border bg-warning-50 px-5 py-2.5">
          <AlertTriangle className="size-4 shrink-0 text-warning-700" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-warning-700">
              Call audio stopped
            </p>
            <p className="text-xs text-warning-700/90">
              The shared call tab stopped sharing. Your microphone is still
              recording, but nobody on the call is being captured.
            </p>
          </div>
          {onReshareCallAudio && (
            <Button
              type="button"
              size="sm"
              onClick={onReshareCallAudio}
              className="shrink-0"
            >
              Re-share call tab
            </Button>
          )}
        </div>
      )}

      {/* Controls row (unified across expanded/collapsed/paused states per the
          MDRS-113 redesign): left = circle play/pause toggle + timer + status
          pill; right = Cancel link + Finish Recording + waveform caret. Only
          the waveform block below collapses. */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 px-5 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={isPaused ? onResume : onPause}
            disabled={isConnecting}
            aria-label={isPaused ? "Resume recording" : "Pause recording"}
            className="grid size-9 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          >
            {isPaused ? (
              <PlayIcon className="size-4 [&_path]:fill-primary-foreground [&_path]:stroke-primary-foreground" />
            ) : (
              <PauseIcon className="size-4 [&_path]:fill-primary-foreground [&_path]:stroke-primary-foreground" />
            )}
          </button>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-3">
              <span className="text-2xl font-semibold tabular-nums text-foreground">
                {formatDuration(elapsedMs)}
              </span>
              {/* Status pill: green "Recording" while live, amber "Paused" when
                  suspended (design tokens success-* / warning-*), neutral
                  "Connecting" before capture starts — never a live dot then. */}
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                  isConnecting
                    ? "bg-background-muted text-foreground-muted"
                    : isPaused
                      ? "bg-warning-50 text-warning-700"
                      : "bg-success-50 text-success-700"
                )}
              >
                <span
                  className={cn(
                    "size-1.5 rounded-full",
                    isConnecting
                      ? "bg-foreground-muted"
                      : isPaused
                        ? "bg-warning-700"
                        : "animate-pulse bg-success-700"
                  )}
                  aria-hidden="true"
                />
                {isConnecting ? "Connecting" : isPaused ? "Paused" : "Recording"}
              </span>
            </div>
            {sessionMode === "telehealth" && (
              <span className="text-xs text-foreground-muted">
                Microphone and call audio, saved continuously
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isConnecting}
            className="text-foreground-muted hover:text-foreground"
          >
            Cancel
          </Button>
          <Button size="sm" onClick={onStop} disabled={isConnecting}>
            Finish Recording
          </Button>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onToggleWaveform}
                aria-label={waveformCollapsed ? "Show waveform" : "Hide waveform"}
                aria-expanded={!waveformCollapsed}
                className="grid size-9 place-items-center rounded-lg border border-border text-foreground-muted transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {waveformCollapsed ? (
                  <ChevronUp className="size-4" />
                ) : (
                  <ChevronDown className="size-4" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {waveformCollapsed ? "Show waveform" : "Hide waveform"}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {sessionMode === "telehealth" ? (
        /* Telehealth source cards (MDRS-268): one card per audio source (mic
           + shared call tab), each with its own live waveform (the same
           LiveAudioVisualizer the in-person waveform below uses) — replaces
           the single combined waveform so it's clear both sides of the call
           are being captured. Collapses like the waveform did. */
        <div
          className={cn(
            "shrink-0 overflow-hidden px-5 transition-all",
            waveformCollapsed ? "h-0 pb-0" : "pb-4"
          )}
          aria-hidden={waveformCollapsed}
        >
          <div className="flex flex-wrap items-stretch gap-2">
            <SourceCard
              icon={<Users className="size-4" />}
              name="You"
              device={inputDeviceLabel}
              track={micTrack}
              waveColor={micWaveColor}
              isPaused={isPaused}
            />
            <SourceCard
              icon={<Monitor className="size-4" />}
              name="Everyone on the call"
              device={
                callAudioLost
                  ? "Call audio stopped"
                  : (callAudioSourceLabel ?? "Shared tab audio")
              }
              track={callAudioTrack}
              waveColor={callWaveColor}
              isPaused={isPaused}
            />
          </div>
        </div>
      ) : (
        /* Waveform. The block is visually collapsed (height 0) when the caret is
            toggled, but the LiveAudioVisualizer inside stays MOUNTED for the whole
            session — unmounting it closes its AudioContext on the MediaStream it
            shares with the Transcriber's audio graph, which starved the worklet
            and froze transcription on resume (MDRS-116 regression). So we hide via
            layout, never unmount. */
        <div
          className={cn(
            "shrink-0 space-y-1.5 overflow-hidden px-5 transition-all",
            waveformCollapsed ? "h-0 pb-0" : "pb-4"
          )}
          aria-hidden={waveformCollapsed}
        >
          <div
            ref={waveContainerRef}
            className="relative flex h-16 w-full items-center overflow-hidden"
          >
            {mediaRecorder && waveWidth > 0 && (
              <div className={cn("h-full w-full", isPaused && "invisible")}>
                <LiveAudioVisualizer
                  mediaRecorder={mediaRecorder}
                  width={waveWidth}
                  height={40}
                  barWidth={WAVE_BAR_WIDTH}
                  gap={WAVE_BAR_GAP}
                  barColor={waveColor}
                  backgroundColor="transparent"
                />
              </div>
            )}
            {/* Paused (or before the first frame): the design's flat idle strip,
                laid over the (hidden-but-mounted) live visualizer. */}
            {(isPaused || !mediaRecorder || waveWidth === 0) && (
              <div className="absolute inset-0">
                <PausedWaveform width={waveWidth} />
              </div>
            )}
          </div>
          <p className="text-center text-xs text-foreground-muted">
            Input: {inputDeviceLabel}
          </p>
        </div>
      )}

      {/* Live transcription — full-bleed subheader bar + flush feed. The feed's
          status footer shows "Paused …" while paused and "Listening…" while
          recording with no interim yet (so the post-resume gap doesn't look
          frozen). */}
      <LiveTranscriptionFeed
        segments={segments}
        interim={interim}
        showSpeakers={showLiveSpeakers}
        state={
          isConnecting
            ? "connecting"
            : isRestoringFeed
              ? "restoring"
              : isPaused
                ? "paused"
                : "listening"
        }
      />

      {/* Notes — full-bleed subheader bar + flush textarea */}
      <div className="flex shrink-0 flex-col">
        <div className="flex items-center justify-between border-y border-border bg-background-raised px-5 py-3">
          <h3
            id="scribe-notes-heading"
            className="text-sm font-semibold text-background-dark"
          >
            Notes
          </h3>
          <span
            className={cn(
              "text-xs",
              notesSaveState === "error"
                ? "text-destructive"
                : "text-foreground-muted"
            )}
            aria-live="polite"
          >
            {savedLabel}
          </span>
        </div>
        <textarea
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Type notes during the session..."
          rows={3}
          aria-labelledby="scribe-notes-heading"
          className="w-full resize-none border-0 bg-background-white px-4 py-3.5 text-sm text-foreground placeholder:text-foreground-muted focus-visible:outline-none focus-visible:ring-0"
        />
      </div>
    </div>
  );
};

/**
 * One telehealth audio-source card (MDRS-268) — "You" (mic) or "Everyone on
 * the call" (shared call-tab audio) — with an icon, name, device/status
 * caption, and its own live waveform (TrackWaveform — the same single-source
 * meter the Audio Setup step's mic picker uses).
 */
const SourceCard = ({
  icon,
  name,
  device,
  track,
  waveColor,
  isPaused,
}: {
  icon: React.ReactNode;
  name: string;
  device: string;
  track: MediaStreamTrack | null;
  waveColor: string;
  isPaused: boolean;
}) => (
  <div className="flex min-w-[220px] flex-1 items-center gap-2.5 rounded-lg bg-background-raised px-3 py-2.5">
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-background-white text-foreground-muted">
      {icon}
    </span>
    <div className="min-w-0 flex-1">
      <p className="truncate text-xs font-semibold text-foreground">{name}</p>
      <p className="truncate text-[11px] text-foreground-muted">{device}</p>
    </div>
    <TrackWaveform track={track} color={waveColor} paused={isPaused} />
  </div>
);

// Paused idle waveform geometry — a thin centered dotted line (the design's
// "no signal" placeholder), independent of the taller live-bar geometry.
const PAUSED_DOT_SIZE = 2; // px
const PAUSED_DOT_GAP = 3; // px

/**
 * The flat idle waveform shown while the session is paused (MDRS-116). The live
 * analyser reads silence when the MediaRecorder is paused, so instead of an
 * empty canvas we render a single centered row of small light-gray dots — the
 * design's calm "paused / no signal" placeholder (a thin dotted line, not the
 * tall live bars).
 */
const PausedWaveform = ({ width }: { width: number }) => {
  const pitch = PAUSED_DOT_SIZE + PAUSED_DOT_GAP;
  const dotCount = width > 0 ? Math.floor(width / pitch) : 0;
  return (
    <div
      className="flex h-16 w-full items-center justify-center overflow-hidden"
      style={{ gap: `${PAUSED_DOT_GAP}px` }}
      aria-label="Recording paused"
      role="img"
    >
      {Array.from({ length: dotCount }).map((_, i) => (
        <span
          key={i}
          className="shrink-0 rounded-full bg-foreground-muted/40"
          style={{ width: `${PAUSED_DOT_SIZE}px`, height: `${PAUSED_DOT_SIZE}px` }}
        />
      ))}
    </div>
  );
};
